// Admin order detail (/admin/orders/:id) — frozen amounts, webhook trail, the
// license the order granted, and the raw provider payload for debugging.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fieldErrorClass } from "@ahcut/ui/recipes";
import {
  Badge,
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Field,
} from "@/ui";
import { Loading, ErrorState } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { MachineBinding } from "@/components/MachineBinding";
import { useAsync } from "@/lib/useAsync";
import { getAdminOrder, refundAdminOrder } from "@/lib/admin";
import {
  formatDateTime,
  formatRupiah,
  licenseStateLabel,
  licenseStateTone,
  orderStatusLabel,
  orderStatusTone,
  tierLabel,
  webhookStatusLabel,
  webhookStatusTone,
} from "@/lib/format";
import type { AdminOrderDetail } from "@/lib/types";
import { cn } from "@/lib/cn";
import { AdminPanel, PanelBody, PanelEmpty } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, NoValue, type DataColumn } from "@/components/admin/DataTable";
import { DetailHeader } from "@/components/DetailHeader";
import { dataCellClass, dataIdClass, factsGridClass, panelInsetClass } from "@/components/admin/classes";
import { BreakableText } from "@/components/admin/BreakableText";

const REFUND_HINT_ID = "admin-order-refund-hint";

const EVENT_COLUMNS: readonly DataColumn[] = [
  { label: "Diterima" },
  { label: "Event" },
  { label: "Status" },
  { label: "Diproses" },
];

export function AdminOrderDetailPage() {
  const { id = "" } = useParams();

  const { data, loading, error, reload } = useAsync<AdminOrderDetail>(
    (s) => getAdminOrder(id, s),
    [id],
  );

  // Refund action state (ConfirmDialog + optional audit reason).
  const [confirmRefund, setConfirmRefund] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  if (loading && !data) return <Loading label="Memuat pesanan…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <ErrorState error={new Error("Pesanan tidak ditemukan.")} onRetry={reload} />;

  const o = data.order;
  const refundable = o.status === "paid";

  async function runRefund() {
    setRefundBusy(true);
    setRefundError(null);
    try {
      await refundAdminOrder(o.id, refundReason);
      setConfirmRefund(false);
      reload();
    } catch (err) {
      setRefundError(err instanceof Error ? err.message : String(err));
    } finally {
      setRefundBusy(false);
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <DetailHeader
        backTo="/admin/orders"
        backLabel="Semua pesanan"
        title={`Pesanan ${tierLabel(o.tier)}`}
        hint={refundable ? undefined : "Hanya pesanan lunas yang bisa ditandai refund."}
        hintId={REFUND_HINT_ID}
        actions={
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setRefundReason("");
              setRefundError(null);
              setConfirmRefund(true);
            }}
            disabled={!refundable}
            aria-describedby={refundable ? undefined : REFUND_HINT_ID}
            title={
              refundable
                ? "Tandai pesanan ini direfund"
                : "Hanya pesanan lunas yang bisa ditandai refund"
            }
          >
            Tandai refund
          </Button>
        }
      />

      <AdminPanel>
        <PanelBody>
          <DescriptionList className={factsGridClass}>
            <DescriptionTerm>Status</DescriptionTerm>
            <DescriptionDetails>
              <Badge tone={orderStatusTone(o.status)} dot>
                {orderStatusLabel(o.status)}
              </Badge>
            </DescriptionDetails>
            <DescriptionTerm>Pengguna</DescriptionTerm>
            <DescriptionDetails className="break-all">
              <Link to={`/admin/users/${o.user_id}`}>{o.user_email ?? o.user_id}</Link>
            </DescriptionDetails>
            <DescriptionTerm>Jumlah (beku)</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>
              {formatRupiah(o.amount)} {o.currency}
            </DescriptionDetails>
            {o.coupon_code && (
              <>
                <DescriptionTerm>Kupon</DescriptionTerm>
                <DescriptionDetails className={dataIdClass}>{o.coupon_code}</DescriptionDetails>
                <DescriptionTerm>Diskon</DescriptionTerm>
                <DescriptionDetails>
                  <span className={dataIdClass}>
                    {formatRupiah((o.list_amount_minor ?? o.amount) - o.amount)}
                  </span>{" "}
                  <span className="text-sm text-fg-2">
                    (harga awal{" "}
                    <span className={dataIdClass}>
                      {formatRupiah(o.list_amount_minor ?? o.amount)}
                    </span>
                    )
                  </span>
                </DescriptionDetails>
              </>
            )}
            <DescriptionTerm>Provider</DescriptionTerm>
            <DescriptionDetails>{o.provider}</DescriptionDetails>
            <DescriptionTerm>Ref provider</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>
              {o.provider_ref ? <BreakableText text={o.provider_ref} at="id" /> : <NoValue label="Tidak ada" />}
            </DescriptionDetails>
            <DescriptionTerm>Dibuat</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>{formatDateTime(o.created_at)}</DescriptionDetails>
            <DescriptionTerm>Dibayar</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>
              {o.paid_at ? formatDateTime(o.paid_at) : <NoValue label="Belum dibayar" />}
            </DescriptionDetails>
            <DescriptionTerm>ID</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>
              <BreakableText text={o.id} at="id" />
            </DescriptionDetails>
          </DescriptionList>
        </PanelBody>
      </AdminPanel>

      <ConfirmDialog
        open={confirmRefund}
        title="Tandai pesanan direfund?"
        confirmLabel="Tandai refund"
        danger
        busy={refundBusy}
        onConfirm={runRefund}
        onCancel={() => setConfirmRefund(false)}
      >
        <p>
          Pesanan ditandai <strong className="font-medium text-fg-0">Dikembalikan</strong> dan
          lisensi dari pesanan ini dicabut. Pengembalian dananya sendiri dilakukan manual di
          Pakasir — di luar sistem ini.
        </p>
        <Field
          label="Alasan (opsional)"
          value={refundReason}
          onChange={(e) => setRefundReason(e.target.value)}
          placeholder="mis. permintaan pembeli"
        />
        {refundError && (
          <p role="alert" className={fieldErrorClass}>
            {refundError}
          </p>
        )}
      </ConfirmDialog>

      <AdminPanel title="Lisensi dari pesanan ini">
        {data.license ? (
          <PanelBody>
            <DescriptionList className={factsGridClass}>
              <DescriptionTerm>Tier</DescriptionTerm>
              <DescriptionDetails>{tierLabel(data.license.tier)}</DescriptionDetails>
              <DescriptionTerm>Status</DescriptionTerm>
              <DescriptionDetails>
                <Badge tone={licenseStateTone(data.license.revoked_at)} dot>
                  {licenseStateLabel(data.license.revoked_at)}
                </Badge>
              </DescriptionDetails>
              <DescriptionTerm>Perangkat</DescriptionTerm>
              <DescriptionDetails>
                <MachineBinding machineId={data.license.machine_id} />
              </DescriptionDetails>
              <DescriptionTerm>ID lisensi</DescriptionTerm>
              <DescriptionDetails className={dataIdClass}>
                <BreakableText text={data.license.id} at="id" />
              </DescriptionDetails>
            </DescriptionList>
          </PanelBody>
        ) : (
          <PanelEmpty>Belum ada lisensi untuk pesanan ini (belum lunas / gagal).</PanelEmpty>
        )}
      </AdminPanel>

      <AdminPanel title="Riwayat webhook">
        {data.events.length === 0 ? (
          <PanelEmpty>Belum ada event webhook untuk pesanan ini.</PanelEmpty>
        ) : (
          <DataTable label="Riwayat webhook" columns={EVENT_COLUMNS} stackBelow="58rem">
            {data.events.map((e) => (
              <DataRow key={e.id}>
                <DataCell label="Diterima" className={dataCellClass}>
                  {formatDateTime(e.received_at)}
                </DataCell>
                {/* 12px, one line in table layout — the same as /admin/webhooks. */}
                <DataCell label="Event" className={cn(dataCellClass, "text-xs")}>
                  {e.event_id}
                </DataCell>
                <DataCell label="Status">
                  {e.status ? (
                    <Badge tone={webhookStatusTone(e.status)} dot>
                      {webhookStatusLabel(e.status)}
                    </Badge>
                  ) : (
                    <NoValue label="Tanpa status" />
                  )}
                </DataCell>
                <DataCell label="Diproses" className={dataCellClass}>
                  {e.processed_at ? formatDateTime(e.processed_at) : <NoValue label="Belum diproses" />}
                </DataCell>
              </DataRow>
            ))}
          </DataTable>
        )}
      </AdminPanel>

      {data.raw_payload !== undefined && data.raw_payload !== null && (
        <AdminPanel title="Payload mentah">
          <pre
            role="region"
            aria-label="Payload mentah"
            tabIndex={0}
            className={`${panelInsetClass} max-h-80 overflow-auto bg-surface-1 py-4 text-xs leading-5 break-words whitespace-pre-wrap text-fg-1 focus-visible:-outline-offset-2`}
          >
            {JSON.stringify(data.raw_payload, null, 2)}
          </pre>
        </AdminPanel>
      )}
    </div>
  );
}
